import { 
  getData, 
  postData, 
  patchData, 
  deleteData 
} from '../api/api';
import { customerIncludesUser, normalizeCustomerUsers } from '../lib/customer/users';
import { buildIlikeFilter } from '../lib/rsql';
import { buildReadQueryString } from '../lib/api/readQuery';
import { parseBackendNotificationDetails } from '../utils/notification';

const CUSTOMER_BASE_URL = '/customer';
export const CUSTOMER_LOCALIZATION_OVERLAY_PREFIX = "beas.customer.localization:";

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

export function localizationOverlayStorageKey(customerId: string): string {
  return `${CUSTOMER_LOCALIZATION_OVERLAY_PREFIX}${customerId}`;
}

export function persistCustomerLocalizationOverlay(
  customerId: string,
  localization: { timeZone: string; locale: string },
): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(
    localizationOverlayStorageKey(customerId),
    JSON.stringify({
      timeZone: localization.timeZone.trim(),
      locale: localization.locale.trim(),
    }),
  );
}

export function readCustomerLocalizationOverlay(
  customerId: string,
): { timeZone?: string; locale?: string } | null {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(localizationOverlayStorageKey(customerId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { timeZone?: string; locale?: string };
    return {
      timeZone: firstNonEmptyString(parsed.timeZone),
      locale: firstNonEmptyString(parsed.locale),
    };
  } catch {
    return null;
  }
}

export function normalizeCustomerLocalization(customer: Customer | null): Customer | null {
  if (!customer) {
    return null;
  }
  const raw = customer as Customer & {
    timezone?: string;
    time_zone?: string;
    localization?: { timeZone?: string; timezone?: string; locale?: string };
  };
  const overlay = customer.id ? readCustomerLocalizationOverlay(customer.id) : null;
  return {
    ...customer,
    timeZone: firstNonEmptyString(
      overlay?.timeZone,
      raw.timeZone,
      raw.timezone,
      raw.time_zone,
      raw.localization?.timeZone,
      raw.localization?.timezone,
    ),
    locale: firstNonEmptyString(overlay?.locale, raw.locale, raw.localization?.locale),
  };
}

export function buildCustomerUpdatePayload(
  customer: Customer,
  localization: { timeZone: string; locale: string },
): UpdateCustomerRequest {
  if (!customer.id?.trim()) {
    throw new Error("Customer id is required to update localization.");
  }

  const users = normalizeCustomerUsers(customer.users ?? customer.username);
  return {
    id: customer.id,
    name: customer.name?.trim() || "",
    users: users.length > 0 ? users : undefined,
    logo: customer.logo,
    description: customer.description,
    email: customer.email,
    phone: customer.phone,
    mobilePhone: customer.mobilePhone,
    address: customer.address,
    pointOfContact: customer.pointOfContact,
    status: customer.status,
    startWorkTime: customer.startWorkTime,
    endWorkTime: customer.endWorkTime,
    timeZone: localization.timeZone.trim(),
    locale: localization.locale.trim(),
  };
}

function unwrapCustomerResponse(response: unknown, fallback: Customer): Customer {
  if (!response || typeof response !== "object") {
    return fallback;
  }
  const raw = response as Customer & { content?: Customer[] };
  if (Array.isArray(raw.content) && raw.content[0]) {
    return raw.content[0];
  }
  if (raw.id) {
    return raw;
  }
  return fallback;
}

export function getCustomerApiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const details = parseBackendNotificationDetails(response?.data);
  const parts = [details.message, details.reason, response?.status ? `HTTP ${response.status}` : ""]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);
  if (parts.length > 0) {
    return parts.join(" — ");
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export interface Customer {
  id?: string;
  name?: string;
  users?: string[];
  /** @deprecated Legacy API field; normalized via `normalizeCustomerUsers`. */
  username?: string;
  logo?: string; // base64 encoded
  description?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  address?: string;
  pointOfContact?: string;
  status?: string; // ACTIVE, INACTIVE, SUSPENDED
  startWorkTime?: string; // HH:mm format
  endWorkTime?: string; // HH:mm format
  timeZone?: string;
  locale?: string;
  createdDate?: string;
  createdBy?: string;
}

export interface CreateCustomerRequest {
  name: string;
  users?: string[];
  logo?: string;
  description?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  address?: string;
  pointOfContact?: string;
  status?: string;
  startWorkTime?: string;
  endWorkTime?: string;
  timeZone?: string;
  locale?: string;
}

export interface UpdateCustomerRequest extends CreateCustomerRequest {
  id: string;
}

async function readCustomerById(id: string): Promise<Customer | null> {
  const response = await getData(`${CUSTOMER_BASE_URL}/read/${id}`, {
    skipGlobalLoading: true,
    skipGlobalErrorHandler: true,
  });
  const customer = Array.isArray((response as { content?: Customer[] }).content)
    ? (response as { content: Customer[] }).content[0]
    : (response as Customer);
  return customer?.id ? normalizeCustomerLocalization(customer) : null;
}

async function hydrateCustomerLocalization(customer: Customer | null): Promise<Customer | null> {
  if (!customer?.id || customer.timeZone?.trim()) {
    return customer;
  }
  try {
    const full = await readCustomerById(customer.id);
    if (!full) {
      return customer;
    }
    return normalizeCustomerLocalization({ ...customer, ...full });
  } catch {
    return customer;
  }
}

export const customerService = {
  // Get all customers
  getCustomers: async (
    page: number = 0,
    size: number = 10,
    search?: string,
    fields?: string | string[],
  ) => {
    const searchParam = buildIlikeFilter('name', search ?? '');

    return getData(
      `${CUSTOMER_BASE_URL}/read?${buildReadQueryString({
        page,
        size,
        sort: 'createdDate,desc',
        search: searchParam || undefined,
        fields,
      })}`,
    );
  },

  // Get specific customer by ID
  getCustomer: async (id: string) => {
    const response = await getData(`${CUSTOMER_BASE_URL}/read/${id}`);
    const customer = Array.isArray((response as { content?: Customer[] }).content)
      ? (response as { content: Customer[] }).content[0]
      : (response as Customer);
    return customer?.id ? normalizeCustomerLocalization(customer) : customer;
  },

  // Customer linked to the authenticated user (tenant profile).
  getMyCustomer: async (username?: string): Promise<Customer | null> => {
    const response = await getData(`${CUSTOMER_BASE_URL}/my`, {
      skipGlobalLoading: true,
      skipGlobalErrorHandler: true,
    });

    if (!response) {
      return null;
    }

    if (Array.isArray((response as { content?: Customer[] }).content)) {
      const items = (response as { content: Customer[] }).content;
      if (username) {
        const matched = items.find((item) => customerIncludesUser(item, username));
        if (matched) {
          return hydrateCustomerLocalization(normalizeCustomerLocalization(matched));
        }
      }
      return hydrateCustomerLocalization(normalizeCustomerLocalization(items[0] ?? null));
    }

    if ((response as Customer).id) {
      return hydrateCustomerLocalization(normalizeCustomerLocalization(response as Customer));
    }

    return null;
  },

  getCustomerByUsername: async (username: string) => {
    const response = await getData(
      `${CUSTOMER_BASE_URL}/read?${buildReadQueryString({
        page: 0,
        size: 1,
        sort: "createdDate,desc",
        search: `users=includes=${username}`,
      })}`,
      {
        skipGlobalLoading: true,
        skipGlobalErrorHandler: true,
      },
    );
    return response?.content?.[0] ?? null;
  },

  // Create new customer
  createCustomer: async (data: CreateCustomerRequest) => {
    return postData(`${CUSTOMER_BASE_URL}/create`, data);
  },

  // Update customer
  updateCustomer: async (data: UpdateCustomerRequest) => {
    return patchData(`${CUSTOMER_BASE_URL}/update`, data);
  },

  updateMyLocalization: async (
    customer: Customer,
    data: { timeZone: string; locale: string },
  ): Promise<Customer> => {
    const requested = {
      timeZone: data.timeZone.trim(),
      locale: data.locale.trim(),
    };
    const applySaved = (response: unknown): Customer => {
      const merged = normalizeCustomerLocalization(
        unwrapCustomerResponse(response, { ...customer, ...requested }),
      )!;
      const next = {
        ...merged,
        timeZone: requested.timeZone,
        locale: requested.locale,
      };
      if (customer.id) {
        persistCustomerLocalizationOverlay(customer.id, requested);
      }
      return next;
    };

    try {
      const response = await patchData(`${CUSTOMER_BASE_URL}/my`, requested, {
        skipGlobalErrorHandler: true,
      });
      return applySaved(response);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 403 && status !== 404 && status !== 405) {
        throw error;
      }

      const payload = buildCustomerUpdatePayload(customer, requested);
      const response = await patchData(`${CUSTOMER_BASE_URL}/update`, payload, {
        skipGlobalErrorHandler: true,
      });
      return applySaved(response);
    }
  },

  // Delete customer
  deleteCustomer: async (id: string) => {
    return deleteData(`${CUSTOMER_BASE_URL}/delete/${id}`);
  }
};

